/**
 * Shifts Management Page
 * صفحة إدارة الورديات
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';

const I = {
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  moon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
};

const DAYS = ['أحد', 'اثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];

function formatTime(t) {
  if (!t) return '--:--';
  return t.slice(0, 5);
}

function getDaysFromMask(mask) {
  if (!mask) return [];
  return DAYS.filter((_, i) => mask & (1 << i));
}

const EMPTY_FORM = { name: '', name_ar: '', start_time: '08:00', end_time: '16:00' };

const ShiftsPage = () => {
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftDetail, setShiftDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadShifts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/tenant/shifts');
      setShifts(data || []);
    } catch {
      showError('فشل تحميل الورديات');
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  useEffect(() => { loadShifts(); }, [loadShifts]);

  const loadShiftDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const data = await apiCall(`/tenant/shifts/${id}`);
      setShiftDetail(data);
    } catch {
      showError('فشل تحميل تفاصيل الوردية');
    } finally {
      setDetailLoading(false);
    }
  }, [apiCall, showError]);

  const openCreate = () => {
    setEditingShift(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (shift, e) => {
    e.stopPropagation();
    setEditingShift(shift);
    setForm({ name: shift.name, name_ar: shift.name_ar || '', start_time: formatTime(shift.start_time), end_time: formatTime(shift.end_time) });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_time || !form.end_time) { showError('يرجى ملء جميع الحقول'); return; }
    setSubmitting(true);
    try {
      if (editingShift) {
        await apiCall(`/tenant/shifts/${editingShift.id}`, { method: 'PATCH', body: JSON.stringify(form) });
        success('تم تحديث الوردية');
      } else {
        await apiCall('/tenant/shifts', { method: 'POST', body: JSON.stringify(form) });
        success('تم إنشاء الوردية');
      }
      setShowModal(false);
      loadShifts();
      if (selectedShift && editingShift?.id === selectedShift) loadShiftDetail(selectedShift);
    } catch {
      showError('فشل حفظ الوردية');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (shift, e) => {
    e.stopPropagation();
    try {
      await apiCall(`/tenant/shifts/${shift.id}`, { method: 'PATCH', body: JSON.stringify({ is_active: !shift.is_active }) });
      success(shift.is_active ? 'تم تعطيل الوردية' : 'تم تفعيل الوردية');
      loadShifts();
    } catch {
      showError('فشل تحديث الوردية');
    }
  };

  const handleSelectShift = (id) => {
    setSelectedShift(id);
    loadShiftDetail(id);
  };

  const calcDuration = (start, end) => {
    if (!start || !end) return null;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60;
    const h = Math.floor(mins / 60), m = mins % 60;
    return `${h} ساعة${m ? ` و${m} دقيقة` : ''}`;
  };

  return (
    <div className="page" dir="rtl">
      <style>{`
        .shifts-layout { display: grid; grid-template-columns: 1fr 340px; gap: 20px; }
        @media(max-width:900px){ .shifts-layout{ grid-template-columns:1fr; } }
        .shifts-grid { display: flex; flex-direction: column; gap: 12px; }
        .shift-card { background: var(--tenant-card-bg); border: 1px solid var(--tenant-border); border-radius: 12px; padding: 16px 20px; cursor: pointer; transition: all .2s; }
        .shift-card:hover, .shift-card.selected { border-color: var(--tenant-primary); box-shadow: 0 0 0 2px rgba(var(--tenant-primary-rgb),.1); }
        .shift-card.inactive { opacity: .6; }
        .shift-card__header { display: flex; align-items: center; justify-content: space-between; }
        .shift-card__name { font-weight: 600; font-size: 15px; }
        .shift-card__time { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 13px; color: var(--tenant-text-muted); }
        .shift-card__time strong { color: var(--tenant-text); font-size: 15px; }
        .shift-card__meta { display: flex; gap: 12px; margin-top: 10px; font-size: 12px; }
        .shift-card__actions { display: flex; gap: 6px; }
        .badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; }
        .badge--active { background: #dcfce7; color: #166534; }
        .badge--inactive { background: #fee2e2; color: #991b1b; }
        .badge--drivers { background: #ede9fe; color: #5b21b6; }
        .badge--night { background: #1e1b4b; color: #c7d2fe; }
        .detail-panel { background: var(--tenant-card-bg); border: 1px solid var(--tenant-border); border-radius: 12px; padding: 20px; height: fit-content; }
        .detail-panel h3 { font-size: 15px; font-weight: 600; margin-bottom: 16px; }
        .detail-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--tenant-border); font-size: 13px; }
        .detail-row:last-child { border-bottom: none; }
        .drivers-list { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
        .driver-item { display: flex; align-items: center; gap: 10px; padding: 8px 12px; background: var(--tenant-bg); border-radius: 8px; font-size: 13px; }
        .driver-avatar { width: 32px; height: 32px; border-radius: 50%; background: var(--tenant-primary-light); display: flex; align-items: center; justify-content: center; font-weight: 700; color: var(--tenant-primary); font-size: 13px; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal { background: var(--tenant-card-bg); border-radius: 16px; padding: 28px; width: 420px; max-width: 95vw; }
        .modal h2 { font-size: 17px; font-weight: 700; margin-bottom: 20px; }
        .form-field { margin-bottom: 16px; }
        .form-field label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 6px; }
        .form-field input { width: 100%; padding: 10px 12px; border: 1px solid var(--tenant-border); border-radius: 8px; background: var(--tenant-bg); color: var(--tenant-text); font-size: 14px; box-sizing: border-box; }
        .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .duration-badge { font-size: 12px; color: var(--tenant-text-muted); margin-top: 4px; }
        .empty-detail { text-align: center; padding: 40px 20px; color: var(--tenant-text-muted); }
        .empty-detail svg { margin-bottom: 8px; opacity: .4; }
        .stat-pill { background: var(--tenant-primary-light,#ede9fe); color: var(--tenant-primary); border-radius: 8px; padding: 8px 14px; text-align: center; }
        .stat-pill .num { font-size: 22px; font-weight: 700; display: block; }
        .stat-pill .lbl { font-size: 11px; }
        .stats-row { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; margin-bottom: 16px; }
      `}</style>

      {/* Header */}
      <div className="page-header">
        <div className="page-header__title">
          {I.clock}
          <div>
            <h1>إدارة الورديات</h1>
            <p>{shifts.length} وردية مسجلة</p>
          </div>
        </div>
        <button className="btn btn--primary" onClick={openCreate}>{I.plus} إنشاء وردية</button>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-pill">
          <span className="num">{shifts.filter(s => s.is_active).length}</span>
          <span className="lbl">ورديات نشطة</span>
        </div>
        <div className="stat-pill">
          <span className="num">{shifts.reduce((a, s) => a + (s.assigned_drivers || 0), 0)}</span>
          <span className="lbl">سائق مُعيَّن</span>
        </div>
        <div className="stat-pill">
          <span className="num">{shifts.filter(s => s.crosses_midnight).length}</span>
          <span className="lbl">ورديات ليلية</span>
        </div>
      </div>

      <div className="shifts-layout">
        {/* Shifts List */}
        <div>
          {loading ? (
            <div className="page-loading"><div className="spinner" /></div>
          ) : shifts.length === 0 ? (
            <div className="page-empty">{I.clock}<h3>لا توجد ورديات</h3><p>ابدأ بإنشاء وردية جديدة</p></div>
          ) : (
            <div className="shifts-grid">
              {shifts.map(shift => (
                <div
                  key={shift.id}
                  className={`shift-card ${!shift.is_active ? 'inactive' : ''} ${selectedShift === shift.id ? 'selected' : ''}`}
                  onClick={() => handleSelectShift(shift.id)}
                >
                  <div className="shift-card__header">
                    <span className="shift-card__name">{shift.name_ar || shift.name}</span>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {shift.crosses_midnight && <span className="badge badge--night">{I.moon} ليلية</span>}
                      <span className={`badge ${shift.is_active ? 'badge--active' : 'badge--inactive'}`}>
                        {shift.is_active ? 'نشطة' : 'معطلة'}
                      </span>
                    </div>
                  </div>
                  <div className="shift-card__time">
                    <strong>{formatTime(shift.start_time)}</strong>
                    <span>←</span>
                    <strong>{formatTime(shift.end_time)}</strong>
                    <span>({calcDuration(shift.start_time, shift.end_time)})</span>
                  </div>
                  <div className="shift-card__meta">
                    <span className="badge badge--drivers">{I.user} {shift.assigned_drivers || 0} سائق</span>
                    <div className="shift-card__actions" onClick={e => e.stopPropagation()}>
                      <button className="btn btn--ghost btn--sm" onClick={(e) => openEdit(shift, e)}>{I.edit}</button>
                      <button
                        className={`btn btn--sm ${shift.is_active ? 'btn--danger' : 'btn--success'}`}
                        style={{ fontSize: 11 }}
                        onClick={(e) => handleToggleActive(shift, e)}
                      >
                        {shift.is_active ? 'تعطيل' : 'تفعيل'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="detail-panel">
          {!selectedShift ? (
            <div className="empty-detail">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <p>اختر وردية لعرض تفاصيلها</p>
            </div>
          ) : detailLoading ? (
            <div className="page-loading"><div className="spinner" /></div>
          ) : shiftDetail ? (
            <>
              <h3>{shiftDetail.name_ar || shiftDetail.name}</h3>
              <div className="detail-row"><span>وقت البداية</span><strong>{formatTime(shiftDetail.start_time)}</strong></div>
              <div className="detail-row"><span>وقت النهاية</span><strong>{formatTime(shiftDetail.end_time)}</strong></div>
              <div className="detail-row"><span>المدة</span><strong>{calcDuration(shiftDetail.start_time, shiftDetail.end_time)}</strong></div>
              <div className="detail-row"><span>ليلية</span><strong>{shiftDetail.crosses_midnight ? 'نعم' : 'لا'}</strong></div>
              <div className="detail-row"><span>الحالة</span>
                <span className={`badge ${shiftDetail.is_active ? 'badge--active' : 'badge--inactive'}`}>
                  {shiftDetail.is_active ? 'نشطة' : 'معطلة'}
                </span>
              </div>

              <div style={{ marginTop: 20 }}>
                <h3>السائقون المعينون ({shiftDetail.drivers?.length || 0})</h3>
                {shiftDetail.drivers?.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--tenant-text-muted)', textAlign: 'center', padding: '16px 0' }}>لا يوجد سائقون معينون لهذه الوردية</p>
                ) : (
                  <div className="drivers-list">
                    {shiftDetail.drivers?.map(d => (
                      <div key={d.id} className="driver-item">
                        <div className="driver-avatar">{d.full_name?.[0] || '?'}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{d.full_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--tenant-text-muted)' }}>
                            أيام: {getDaysFromMask(d.days_mask).join('، ') || 'كل الأيام'}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--tenant-text-muted)' }}>{d.phone}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2>{editingShift ? 'تعديل الوردية' : 'إنشاء وردية جديدة'}</h2>
              <button className="btn btn--ghost btn--sm" onClick={() => setShowModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-field">
                <label>الاسم بالعربي</label>
                <input value={form.name_ar} onChange={e => setForm(f => ({ ...f, name_ar: e.target.value }))} placeholder="مثال: الوردية الصباحية" />
              </div>
              <div className="form-field">
                <label>الاسم بالإنجليزي</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Morning Shift" required />
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>وقت البداية</label>
                  <input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} required />
                </div>
                <div className="form-field">
                  <label>وقت النهاية</label>
                  <input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} required />
                </div>
              </div>
              {form.start_time && form.end_time && (
                <div className="duration-badge">المدة: {calcDuration(form.start_time, form.end_time)}</div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="submit" className="btn btn--primary" style={{ flex: 1 }} disabled={submitting}>
                  {submitting ? 'جاري الحفظ...' : editingShift ? 'حفظ التعديلات' : 'إنشاء الوردية'}
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => setShowModal(false)}>إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftsPage;
